/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from'react';
import { cn } from'@/shared/ui/utils/tailwind';
import { useTranslation } from'react-i18next';

export type NotificationType ='success' |'error' |'warning' |'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string | ReactNode;
  autoClose?: number | false;
  onClose?: () => void;
}

interface NotificationContextType {
  notifications: Notification[];
  show: (notification: Omit<Notification,'id'>) => string;
  hide: (id: string) => void;
  clean: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

/**
 * Provides access to the notification context for showing, hiding, and clearing notifications.
 *
 * @returns {NotificationContextType} Notification methods.
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
  position?:'top-right' |'top-left' |'bottom-right' |'bottom-left' |'top-center' |'bottom-center';
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  position ='top-right',
}) => {
  const { t } = useTranslation('common');
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const show = useCallback((notification: Omit<Notification,'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newNotification: Notification = {
      ...notification,
      id,
      autoClose: notification.autoClose ?? 4000,
    };

    setNotifications((prev) => [...prev, newNotification]);

    if (newNotification.autoClose && newNotification.autoClose > 0) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        notification.onClose?.();
      }, newNotification.autoClose);
    }

    return id;
  }, []);

  const hide = useCallback((id: string) => {
    setNotifications((prev) => {
      const notification = prev.find((n) => n.id === id);
      notification?.onClose?.();
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  const clean = useCallback(() => {
    setNotifications([]);
  }, []);

  // Set global instance for backward compatibility
  useEffect(() => {
    globalNotifications = { notifications, show, hide, clean };
    return () => {
      globalNotifications = null;
    };
  }, [notifications, show, hide, clean]);

  const positionClasses = {
    'top-right':'top-4 right-4',
    'top-left':'top-4 left-4',
    'bottom-right':'bottom-4 right-4',
    'bottom-left':'bottom-4 left-4',
    'top-center':'top-4 left-1/2 -translate-x-1/2',
    'bottom-center':'bottom-4 left-1/2 -translate-x-1/2',
  };

  const typeColors = {
    success:'bg-[var(--status-success)]',
    error:'bg-[var(--status-error)]',
    warning:'bg-[var(--status-warning)]',
    info:'bg-[var(--status-info)]',
  };

  return (
    <NotificationContext.Provider value={{ notifications, show, hide, clean }}>
      {children}
      <div
        data-testid="notification-viewport"
        className={cn(
          'fixed z-[10003] flex flex-col gap-2 w-full pointer-events-none',
          'max-w-[min(24rem,calc(100vw-2rem))]',
          positionClasses[position]
        )}
      >
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={cn(
              'flex items-start gap-3 p-4 rounded-(--radius-lg) shadow-lg pointer-events-auto',
              'bg-[var(--surface-primary)] border border-[var(--border-subtle)]',
              '',
              'animate-in slide-in-from-right-full duration-300'
            )}
          >
            <div className={cn('w-1 h-full rounded-full flex-shrink-0', typeColors[notification.type])} />
            <div className="flex-1 min-w-0">
              {notification.title && (
                <div className="font-semibold text-sm text-[var(--text-primary)] mb-1">
                  {notification.title}
                </div>
              )}
              <div className="text-sm text-[var(--text-secondary)] line-clamp-3">
                {notification.message}
              </div>
            </div>
            <button
              onClick={() => hide(notification.id)}
              className="flex-shrink-0 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              aria-label={t('form.aria.closeNotification')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

// Global notification instance for backward compatibility
let globalNotifications: NotificationContextType | null = null;

export const notifications = {
  show: (config: any) => {
    if (!globalNotifications) {
      console.warn('NotificationProvider not found. Please wrap your app with NotificationProvider.');
      return'';
    }
    // Map legacy Mantine color names to our notification types
    const colorMap: Record<string, NotificationType> = {
      green:'success',
      red:'error',
      yellow:'warning',
      blue:'info',
    };
    const validTypes: NotificationType[] = ['success','error','warning','info'];
    const type: NotificationType =
      (validTypes.includes(config.type) ? config.type : null) ||
      colorMap[config.color] ||
      'info';
    return globalNotifications.show({
      type,
      title: config.title,
      message: config.message,
      autoClose: config.autoClose,
      onClose: config.onClose,
    });
  },
  hide: (id: string) => {
    globalNotifications?.hide(id);
  },
  clean: () => {
    globalNotifications?.clean();
  },
};

